import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Phone, TrendingUp, Sparkles } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { StickyCTABar } from "@/components/StickyCTABar";
import { FinalCTA } from "@/components/home/HomeSections";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  type CustomIndustryServiceEntry,
  resolveCustomEntryContext,
} from "@/lib/customIndustryServiceData";
import {
  getSiblingServiceCombosForIndustry,
  getSiblingIndustryCombosForService,
} from "@/lib/industryServiceData";
import {
  SITE_URL,
  SCHEMA_LOCALBUSINESS_ID,
  LOCAL_BUSINESS_SCHEMA,
  buildSpeakableSchema,
} from "@/lib/entitySchema";
import { ogImageFor, ogMetaTags } from "@/lib/ogImages";
import { StatewideCoverageStrip } from "@/components/StatewideCoverageStrip";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { FreeAuditCTABanner } from "@/components/FreeAuditCTABanner";
import { MeetChrisStrip } from "@/components/MeetChrisStrip";

type Props = { entry: CustomIndustryServiceEntry };

export function buildCustomIndustryServiceHead(entry: CustomIndustryServiceEntry) {
  const url = `${SITE_URL}/${entry.slug}`;
  const og = ogImageFor({});
  return {
    meta: [
      { title: entry.metaTitle },
      { name: "description", content: entry.metaDescription },
      { property: "og:title", content: entry.metaTitle },
      { property: "og:description", content: entry.metaDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      ...ogMetaTags(og),
      { name: "twitter:title", content: entry.metaTitle },
      { name: "twitter:description", content: entry.metaDescription },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

export function CustomIndustryServicePage({ entry }: Props) {
  const ctx = resolveCustomEntryContext(entry);
  if (!ctx) return null;
  const { service, industry } = ctx;
  const path = `/${entry.slug}`;
  const pageUrl = `${SITE_URL}${path}`;

  const siblingServices = getSiblingServiceCombosForIndustry(
    industry.slug,
    service.slug,
  );
  const siblingIndustries = getSiblingIndustryCombosForService(
    service.slug,
    industry.slug,
  );

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Industries", item: `${SITE_URL}/industries` },
      { "@type": "ListItem", position: 3, name: industry.name, item: `${SITE_URL}/industries/${industry.slug}` },
      { "@type": "ListItem", position: 4, name: `${service.serviceName} for ${industry.name}`, item: pageUrl },
    ],
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    name: `${service.serviceName} for ${industry.name}`,
    serviceType: service.serviceName,
    description: entry.metaDescription,
    url: pageUrl,
    areaServed: { "@type": "State", name: "Colorado" },
    provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
    audience: {
      "@type": "BusinessAudience",
      audienceType: industry.shortName,
      name: `${industry.name} businesses`,
    },
    category: industry.name,
  };

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${pageUrl}#article`,
    headline: entry.metaTitle,
    description: entry.metaDescription,
    mainEntityOfPage: pageUrl,
    image: `${SITE_URL}/logo.png`,
    datePublished: "2025-01-01",
    dateModified: new Date().toISOString().slice(0, 10),
    author: { "@id": SCHEMA_LOCALBUSINESS_ID },
    publisher: { "@id": SCHEMA_LOCALBUSINESS_ID },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    mainEntity: entry.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const webPageJsonLd = {
    ...buildSpeakableSchema(pageUrl, entry.metaTitle),
    "@id": `${pageUrl}#webpage`,
    description: entry.metaDescription,
    breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
    mainEntity: { "@id": `${pageUrl}#service` },
  };

  return (
    <SiteLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LOCAL_BUSINESS_SCHEMA) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }} />

      <PageHero
        eyebrow={entry.heroEyebrow}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Industries", to: "/industries" },
          { label: industry.name, to: `/industries/${industry.slug}` },
          { label: `${service.serviceName} for ${industry.shortName}` },
        ]}
        title={
          <>
            {entry.heroTitle.lead}
            <span className="text-gradient-gold">{entry.heroTitle.gold}</span>
            {entry.heroTitle.trail}
          </>
        }
        description={entry.heroDescription}
      >
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a free strategy call
            <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-5 w-5" /> Call now
          </a>
        </div>
      </PageHero>

      <main className="bg-brand-black">
        {/* TL;DR — answer-engine box */}
        <section className="border-b border-white/10 bg-white/[0.02] py-12">
          <div className="mx-auto max-w-3xl px-6">
            <div className="flex items-center gap-2 text-brand-gold">
              <Sparkles className="h-5 w-5" />
              <span className="text-sm font-bold uppercase tracking-wider">TL;DR</span>
            </div>
            <p className="mt-3 text-lg leading-relaxed text-white/85">{entry.tldr}</p>
          </div>
        </section>

        {/* Long-form sections */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-6 space-y-14">
            {entry.sections.map((s) => (
              <article key={s.heading}>
                <h2 className="text-2xl font-bold text-white sm:text-3xl">{s.heading}</h2>
                <div className="mt-5 space-y-5 text-base leading-relaxed text-white/80">
                  {s.body.split("\n\n").map((p, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Keyword research callout */}
        <section className="border-y border-white/10 bg-white/[0.02] py-16">
          <div className="mx-auto max-w-4xl px-6">
            <div className="flex items-center gap-2 text-brand-gold">
              <TrendingUp className="h-5 w-5" />
              <span className="text-sm font-bold uppercase tracking-wider">Real keyword data</span>
            </div>
            <h2 className="mt-3 text-2xl font-bold text-white sm:text-3xl">{entry.keywordResearch.headline}</h2>
            <p className="mt-3 text-base text-white/90">{entry.keywordResearch.intro}</p>
            <div className="mt-8 overflow-x-auto rounded-lg border border-white/10">
              <table className="min-w-full text-sm">
                <thead className="bg-white/5 text-left text-white/90">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Keyword</th>
                    <th className="px-4 py-3 font-semibold">Monthly volume</th>
                    <th className="px-4 py-3 font-semibold">Difficulty</th>
                    <th className="px-4 py-3 font-semibold">Intent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 text-white/85">
                  {entry.keywordResearch.rows.map((r) => (
                    <tr key={r.keyword}>
                      <td className="px-4 py-3 font-medium">{r.keyword}</td>
                      <td className="px-4 py-3">{r.monthlyVolume.toLocaleString()}</td>
                      <td className="px-4 py-3 capitalize">{r.difficulty}</td>
                      <td className="px-4 py-3 capitalize">{r.intent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-base italic text-white/90">{entry.keywordResearch.insight}</p>
          </div>
        </section>

        {/* Case study */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-6">
            <div className="text-sm font-bold uppercase tracking-wider text-brand-gold">Case study</div>
            <h2 className="mt-3 text-2xl font-bold text-white sm:text-3xl">{entry.caseStudy.client}</h2>
            <div className="mt-1 text-sm text-white/85">{entry.caseStudy.city}</div>

            <div className="mt-6 space-y-4 text-base leading-relaxed text-white/80">
              <p><strong className="text-white">Challenge.</strong> {entry.caseStudy.challenge}</p>
              <div>
                <p className="text-white"><strong>What we did:</strong></p>
                <ul className="mt-3 space-y-2">
                  {entry.caseStudy.approach.map((a) => (
                    <li key={a} className="flex gap-2">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-brand-gold" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {entry.caseStudy.results.map((r) => (
                <div key={r.metric} className="rounded-lg border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-xs uppercase tracking-wide text-white/85">{r.metric}</div>
                  <div className="mt-1 text-xl font-bold text-brand-gold">{r.value}</div>
                </div>
              ))}
            </div>

            {entry.caseStudy.pullQuote && (
              <blockquote className="mt-8 border-l-4 border-brand-gold pl-5 text-lg italic text-white/85">
                "{entry.caseStudy.pullQuote}"
              </blockquote>
            )}
          </div>
        </section>

        {/* FAQs */}
        <section className="border-t border-white/10 bg-white/[0.02] py-16">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-6">
              {entry.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left text-white hover:text-brand-gold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/80">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <PricingTeaserStrip />

        <FreeAuditCTABanner
          headline={`Get your free ${industry.shortName} ${service.serviceName.toLowerCase()} audit`}
        />

        <StatewideCoverageStrip
          serviceSlug={service.slug}
          contextLabel={`${service.serviceName.toLowerCase()} for ${industry.shortName}s`}
        />

        {/* Sibling links — internal mesh */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-6 grid gap-8 md:grid-cols-2">
            <div>
              <h3 className="text-lg font-bold text-white">Other services for {industry.shortName}s</h3>
              <ul className="mt-4 space-y-2">
                {siblingServices.map((s) => (
                  <li key={s.path}>
                    <Link to={s.path} className="text-brand-gold hover:underline">
                      {s.service.serviceName} for {industry.name} →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Same service, other industries</h3>
              <ul className="mt-4 space-y-2">
                {siblingIndustries.slice(0, 8).map((s) => (
                  <li key={s.path}>
                    <Link to={s.path} className="text-brand-gold hover:underline">
                      {service.serviceName} for {s.industry.name} →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <MeetChrisStrip />

        <FinalCTA />
      </main>

      <StickyCTABar />
    </SiteLayout>
  );
}
